import type { Cadence } from './plans';

/**
 * Stripe idempotency tokens for plan switches, one per user ACTION (target plan + cadence) rather
 * than per HTTP request.
 *
 * Why the distinction is the whole feature: an UPGRADE charges immediately (it resets
 * `billing_cycle_anchor`, so every accepted update issues a full-price invoice). Two requests
 * carrying the SAME token collapse into one charge at Stripe; two tokens are two charges. So the
 * token has to identify "the switch the user asked for", and survive:
 *
 * - a request that failed WITHOUT a verdict (network drop / timeout / 5xx). We cannot know whether
 *   Stripe charged, the user's natural response is to click again, and that retry must reuse the
 *   token. The confirm dialog closes on failure, which is exactly why the token cannot live in it.
 *
 * ...and be rotated when reusing it would be wrong:
 *
 * - after a DEFINITIVE decline — Stripe replays a cached response for a key for ~24h, including the
 *   decline, so reusing it would trap a user who has since fixed their card (`release`).
 * - after a switch that landed — the next switch is a new action (`release`).
 * - for a different target — a different action, so it never shared the token to begin with.
 *
 * An unresolved action also survives a reload in this tab. A held Stripe update accepts only the
 * ORIGINAL action key, so treating refresh as a new action strands the buyer until Stripe expires
 * the hold. Session storage is deliberately tab-scoped; the action is additionally scoped to the
 * source subscription state so a later switch to the same target cannot borrow an older action.
 */
export interface SwitchActionKeys {
  /** The token for this target — the same one until it is released. */
  keyFor(planId: string, cadence: Cadence, sourceScope?: string): string;
  /** Forget this target's token, so the next attempt is a fresh action. */
  release(planId: string, cadence: Cadence, sourceScope?: string): void;
  /** Forget unresolved actions whose target is now active (for example, payment finished on reload). */
  releaseTarget(planId: string, cadence: Cadence): void;
}

interface KeyStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

interface StoredActionKey {
  key: string;
  planId: string;
  cadence: Cadence;
  sourceScope: string;
}

const STORAGE_KEY = 'symphony.switch-action-keys.v1';

function browserSessionStorage(): KeyStorage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    // Storage can be unavailable under hardened privacy settings. In-memory retries still work.
    return undefined;
  }
}

function readStoredKeys(storage: KeyStorage | undefined): Map<string, StoredActionKey> {
  if (!storage) return new Map();
  try {
    const parsed = JSON.parse(storage.getItem(STORAGE_KEY) ?? '[]') as unknown;
    if (!Array.isArray(parsed)) return new Map();
    const valid = parsed.filter((entry): entry is StoredActionKey => {
      if (!entry || typeof entry !== 'object') return false;
      const candidate = entry as Partial<StoredActionKey>;
      return typeof candidate.key === 'string'
        && typeof candidate.planId === 'string'
        && (candidate.cadence === 'monthly' || candidate.cadence === 'yearly')
        && typeof candidate.sourceScope === 'string';
    });
    return new Map(valid.map((entry) => [actionId(entry.planId, entry.cadence, entry.sourceScope), entry]));
  } catch {
    return new Map();
  }
}

function actionId(planId: string, cadence: Cadence, sourceScope: string): string {
  return `${sourceScope}\u0000${planId}\u0000${cadence}`;
}

export function createSwitchActionKeys(
  mint: () => string = () => crypto.randomUUID(),
  storage: KeyStorage | undefined = browserSessionStorage(),
): SwitchActionKeys {
  const keys = readStoredKeys(storage);
  const persist = () => {
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify([...keys.values()]));
    } catch {
      // A full/blocked store must not break the payment action; this instance still retains the key.
    }
  };
  return {
    keyFor(planId, cadence, sourceScope = '') {
      const id = actionId(planId, cadence, sourceScope);
      const existing = keys.get(id);
      if (existing) return existing.key;
      const entry = { key: mint(), planId, cadence, sourceScope };
      keys.set(id, entry);
      persist();
      return entry.key;
    },
    release(planId, cadence, sourceScope = '') {
      keys.delete(actionId(planId, cadence, sourceScope));
      persist();
    },
    releaseTarget(planId, cadence) {
      for (const [id, entry] of keys) {
        if (entry.planId === planId && entry.cadence === cadence) keys.delete(id);
      }
      persist();
    },
  };
}
